/**
 * NGALocalGeocoder - Geocodificador local usando NGADataService
 * 
 * Busca topónimos en los 233K+ datos pre-descargados del Nomenclátor
 * Geográfico de Andalucía ANTES de recurrir al WFS online.
 * 
 * Ventajas sobre NGAGeocoder (WFS):
 * - Latencia: ~5ms local vs ~200-500ms WFS
 * - Disponibilidad: Funciona offline
 * - Fuzzy matching: uFuzzy optimizado para nombres españoles
 * 
 * Integración en cascada:
 * L2. CDAU local (direcciones)
 * L3. NGA local (topónimos) ← ESTE GEOCODIFICADOR
 * L4. WFS online (fallback si L3 falla)
 * 
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2025-01-29
 */

import { getNGADataService, type NGASearchResult, type NGASearchOptions } from '../../data/NGADataService';
import type { GeocodingResult } from '../../../types/infrastructure';

// =============================================================================
// TIPOS
// =============================================================================

/** Opciones para geocodificación local NGA */
export interface NGALocalGeocodingOptions {
  /** Umbral mínimo de confianza (0-100, default: 60) */
  minConfidence?: number;
  /** Filtrar por tipos INSPIRE */
  types?: Array<'landform' | 'populatedplace' | 'hydrography' | 'building' | 'other'>;
  /** Filtrar por tipos locales (Cortijo, Cueva, etc.) */
  localTypes?: string[];
  /** Límite de candidatos a evaluar */
  limit?: number;
}

/** Resultado interno con metadatos de matching */
interface LocalMatchResult {
  result: GeocodingResult;
  fuzzyScore: number;
  localType: string;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Mapeo de score fuzzy (0-1) a confianza (0-100) */
const FUZZY_TO_CONFIDENCE: Array<[number, number]> = [
  [0.95, 95],  // Casi exacto
  [0.85, 85],  // Muy bueno
  [0.75, 75],  // Bueno
  [0.60, 65],  // Aceptable
  [0.45, 55],  // Bajo
  [0.30, 45],  // Mínimo aceptable
];

/** Tipos locales que indican alta confianza */
/**
 * NGALOC: tiempo durante el que se recuerda que la familia de un municipio no
 * cargó. La validación cruzada llama a `geocode` en todas las filas; sin esta
 * memoria, con R2 caído cada fila pagaría los reintentos de `fetchWithBackoff`.
 */
const FAMILIA_FALLIDA_TTL_MS = 60_000;

const HIGH_CONFIDENCE_TYPES = new Set([
  'Cortijo', 'Casa', 'Ermita', 'Fuente', 'Molino',
  'Cerro', 'Loma', 'Collado', 'Puerto', 'Pico',
  'Aldea', 'Caserío', 'Barriada', 'Pedanía'
]);

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

/**
 * Geocodificador local para topónimos del NGA
 * 
 * Usa NGADataService con índices uFuzzy + Flatbush para búsqueda
 * eficiente en 233K+ topónimos.
 * 
 * @example
 * ```typescript
 * const geocoder = new NGALocalGeocoder();
 * const result = await geocoder.geocode('Cortijo de la Viña', '18047');
 * if (result) {
 *   console.log(`Encontrado: ${result.matchedName} (${result.confidence}%)`);
 * }
 * ```
 */
export class NGALocalGeocoder {
  private initialized = false;
  /** NGALOC: INE cuya familia no cargó → instante del fallo */
  private familiasFallidas = new Map<string, number>();

  /**
   * Geocodifica un topónimo usando datos locales NGA
   * 
   * @param name Nombre del lugar (ej: "Cortijo de la Viña", "Cerro Cementerio")
   * @param codINE Código INE del municipio. NGALOC (19-sep-2026): la búsqueda se
   *   hace **solo en la familia de ese municipio** (`/nga/{ine}/`, única que trae
   *   `codINE`); `nga-all` no lleva municipio y elegía por parecido en toda
   *   Andalucía («Casas Cueva» de 18054 → «Casas Cuevas Viejas», a 80 km,
   *   `OBS-GEO-NGA-LOCAL-ACEPTA-TOPONIMO-A-80-KM-FUERA-DEL-MUNICIPIO`).
   *   Sin `codINE` no hay forma de comprobar el municipio → `null` (DOP-2 bis).
   * @param options Opciones de búsqueda
   * @returns GeocodingResult si encuentra coincidencia, null si no
   */
  async geocode(
    name: string,
    codINE: string,
    options: NGALocalGeocodingOptions = {}
  ): Promise<GeocodingResult | null> {
    const {
      minConfidence = 60,
      types,
      localTypes,
      limit = 5
    } = options;

    if (!name || name.trim().length < 3) {
      return null;
    }

    // NGALOC DOP-2 bis: sin municipio no se propone topónimo.
    const ine = (codINE || '').trim();
    if (!ine) {
      return null;
    }

    try {
      const ngaService = getNGADataService();

      // NGALOC DOP-1/DOP-2 (a): solo topónimos del municipio. Si la familia no
      // carga o viene vacía, no hay candidato (la cascada sigue: WFS, centroide).
      const falloPrevio = this.familiasFallidas.get(ine);
      if (falloPrevio !== undefined && Date.now() - falloPrevio < FAMILIA_FALLIDA_TTL_MS) {
        return null;
      }
      const familia = await ngaService.loadMunicipality(ine);
      if (!familia || familia.length === 0) {
        this.familiasFallidas.set(ine, Date.now());
        return null;
      }
      this.familiasFallidas.delete(ine);

      // Construir opciones para NGADataService
      const searchOptions: NGASearchOptions & { codINE: string } = {
        codINE: ine,
        limit,
        minScore: 0.3, // Score mínimo fuzzy bajo para evaluar
        types: types as NGASearchOptions['types'],
        localTypes
      };

      const results = await ngaService.searchByName(name.trim(), searchOptions);
      
      if (!results || results.length === 0) {
        return null;
      }

      // Evaluar candidatos y seleccionar el mejor
      const bestMatch = this.selectBestMatch(results, name, minConfidence);
      
      if (!bestMatch) {
        return null;
      }

      return bestMatch.result;

    } catch (error) {
      console.warn('[NGALocalGeocoder] Error en búsqueda:', error);
      return null;
    }
  }

  /**
   * Selecciona el mejor candidato de los resultados NGA
   */
  private selectBestMatch(
    results: NGASearchResult[],
    originalName: string,
    minConfidence: number
  ): LocalMatchResult | null {
    let bestMatch: LocalMatchResult | null = null;

    for (const result of results) {
      const confidence = this.calculateConfidence(result, originalName);
      
      if (confidence < minConfidence) {
        continue;
      }

      const match: LocalMatchResult = {
        result: {
          x: result.feature.x,
          y: result.feature.y,
          confidence,
          source: 'nga_local',
          matchedName: result.feature.name,
          // Incluir tipo local en matchType para referencia
          matchType: `toponym:${result.feature.localType}`
        },
        fuzzyScore: result.score,
        localType: result.feature.localType
      };

      if (!bestMatch || confidence > bestMatch.result.confidence) {
        bestMatch = match;
      }
    }

    return bestMatch;
  }

  /**
   * Calcula confianza basada en score fuzzy y tipo local
   */
  private calculateConfidence(result: NGASearchResult, originalName: string): number {
    // Base: convertir score fuzzy a confianza
    let confidence = this.fuzzyScoreToConfidence(result.score);
    
    // Bonus por tipo local de alta confianza
    if (HIGH_CONFIDENCE_TYPES.has(result.feature.localType)) {
      confidence = Math.min(100, confidence + 5);
    }

    // Bonus si el tipo local aparece en el nombre original
    const normalizedName = originalName.toLowerCase();
    const normalizedType = result.feature.localType.toLowerCase();
    if (normalizedName.includes(normalizedType)) {
      confidence = Math.min(100, confidence + 5);
    }

    return Math.round(confidence);
  }

  /**
   * Convierte score fuzzy (0-1) a confianza (0-100)
   */
  private fuzzyScoreToConfidence(score: number): number {
    for (const [threshold, conf] of FUZZY_TO_CONFIDENCE) {
      if (score >= threshold) {
        return conf;
      }
    }
    return 40; // Score muy bajo
  }
}

// =============================================================================
// SINGLETON
// =============================================================================

let instance: NGALocalGeocoder | null = null;

/**
 * Obtiene instancia singleton del geocodificador NGA local
 */
export function getNGALocalGeocoder(): NGALocalGeocoder {
  if (!instance) {
    instance = new NGALocalGeocoder();
  }
  return instance;
}
